import { agentModeAtomFamily } from "@/components/ai/chat/state";
import { AgentMode } from "@/components/ai/prompt-input/agent-mode";
import { cn } from "@/components/podkit/lib/cn";
import { useSetAtom } from "jotai";
import { formatClarifyingQuestionsResponse } from "@/components/ai/clarifying-questions-utils";
import { type FC, useState, useEffect } from "react";
import { FragmentClarifyingQuestions } from "@/components/ai/FragmentClarifyingQuestions";
import { FragmentNextStepsProposal } from "@/components/ai/FragmentNextStepsProposal";
import type { PendingInteractiveFragment } from "@/components/ai/pending-interactive-utils";

type PendingInteractiveBlockProps = {
    fragment: PendingInteractiveFragment | null;
    onSendMessage?: (message: string) => void;
    environmentId?: string;
    className?: string;
};

export const PendingInteractiveBlock: FC<PendingInteractiveBlockProps> = ({ fragment, onSendMessage, environmentId, className }) => {
    const setAgentMode = useSetAtom(agentModeAtomFamily(environmentId ?? ""));
    const [actionTaken, setActionTaken] = useState(false);

    useEffect(() => {
        setActionTaken(false);
    }, [fragment?.id]);

    if (!fragment || actionTaken) return null;

    const handleSendMessage = (message: string) => {
        setActionTaken(true);
        // Eagerly switch to Agent mode so the prompt box updates without waiting for the backend round-trip.
        if (fragment.type === "nextStepsProposal" && environmentId) {
            setAgentMode(AgentMode.Agent);
        }
        onSendMessage?.(message);
    };

    if (fragment.type === "clarifyingQuestions") {
        return (
            <div
                key={fragment.id}
                className={cn(
                    "animate-in fade-in slide-in-from-bottom-2 w-full rounded-t-xl border-t border-border-subtle bg-surface-muted duration-200",
                    className,
                )}
            >
                <FragmentClarifyingQuestions
                    fragment={fragment}
                    onSubmit={(answers) => {
                        const response = formatClarifyingQuestionsResponse(fragment.questions, answers);
                        handleSendMessage(response);
                    }}
                />
            </div>
        );
    }

    return (
        <div
            key={fragment.id}
            className={cn(
                "animate-in fade-in slide-in-from-bottom-2 mb-1 flex w-full justify-end duration-200",
                className,
            )}
        >
            <FragmentNextStepsProposal
                fragment={fragment}
                onActionClick={handleSendMessage}
            />
        </div>
    );
};
